import React from 'react';

interface TopHeaderProps {
  // 왼쪽 영역 (범례 필터)
  legendFilters: {
    pxd: boolean;
    external: boolean;
    intern: boolean;
    planned: boolean;
  };
  onLegendToggle: (type: 'pxd' | 'external' | 'intern' | 'planned') => void;
  
  // 오른쪽 영역 (기존 플로팅 버튼들)
  isEditMode: boolean;
  onEditModeToggle: (editMode: boolean) => void;
  onEditModeCancel?: () => void;
  onSave?: () => void;
  
  // 버전 관련
  currentVersion: any;
  versions: any[];
  showVersionDropdown: boolean;
  onVersionDropdownToggle: () => void;
  onVersionChange: (e: any) => void;
  onVersionSelect: (versionId: number) => void;
  selectedVersions: number[];
  onBulkDeleteVersions: () => void;
  getVersionDisplayText: (version: any) => string;
  
  // 즐겨찾기 관련
  showFavoriteInput: boolean;
  favoriteAlias: string;
  onFavoriteInputToggle: () => void;
  onFavoriteAliasChange: (alias: string) => void;
  onAddFavorite: () => void;
  onRemoveFavorite: () => void;
  
  // 기타
  onDeleteVersion: () => void;
  onOpenPersonManagement: () => void;
}

const TopHeader: React.FC<TopHeaderProps> = ({
  legendFilters,
  onLegendToggle,
  isEditMode,
  onEditModeToggle,
  onEditModeCancel,
  onSave,
  currentVersion,
  versions,
  showVersionDropdown,
  onVersionDropdownToggle,
  onVersionChange,
  onVersionSelect,
  selectedVersions,
  onBulkDeleteVersions,
  getVersionDisplayText,
  showFavoriteInput,
  favoriteAlias,
  onFavoriteInputToggle,
  onFavoriteAliasChange,
  onAddFavorite,
  onRemoveFavorite,
  onDeleteVersion,
  onOpenPersonManagement,
}) => {
  return (
    <>
      <style>
        {`
          .top-header {
            position: fixed;
            top: 0;
            left: 56px;
            right: 0;
            width: calc(100vw - 56px);
            height: 50px;
            background: ${isEditMode ? 'rgba(34, 197, 94, 0.35)' : 'rgba(255, 255, 255, 0.95)'};
            backdrop-filter: blur(8px);
            border-bottom: 1px solid #e5e7eb;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 24px;
            z-index: 500;

          }
          
          .header-left {
            display: flex;
            align-items: center;
            gap: 16px;
          }
          
          .header-right {
            display: flex;
            align-items: center;
            gap: 6px;
          }
          
          .legend-container {
            display: flex;
            gap: 2px;
            align-items: center;
          }
          
          .legend-btn {
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 4px 6px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 12px;
            font-weight: 500;
            color: #374151;
          }
          
          .legend-btn:hover {
            background: #f3f4f6;
            border-color: #9ca3af;
          }
          
          .legend-btn.disabled {
            opacity: 0.4;
            background: #f9fafb;
            border-color: #e5e7eb;
          }
          
          .legend-btn.edit-mode-disabled {
            opacity: 0.5;
            cursor: not-allowed;
            pointer-events: none;
          }
          
          .legend-color {
            width: 16px;
            height: 12px;
            border-radius: 3px;
          }
          
          .legend-color.lg-pxd { background: var(--cc-pxd, rgba(103,143,255,0.26)); border: 1px solid var(--b-pxd, rgba(103,143,255,0.18)); }
          .legend-color.lg-ext { background: var(--cc-ext, rgba(40,183,101,0.55)); border: 1px solid var(--b-ext, rgba(40,183,101,0.4)); }
          .legend-color.lg-intern { background: var(--cc-intern, rgba(255,153,0,0.8)); border: 1px solid var(--b-intern, rgba(255,153,0,0.35)); }
          .legend-color.lg-planned { 
            width: 0; 
            height: 0; 
            border-left: 6px solid transparent; 
            border-top: 6px solid rgb(246,59,226); 
            border-radius: 0;
          }
          
          .control-btn, .primary-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            padding: 6px 12px;
            border: none;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            text-align: center;
          }
          
          .control-btn {
            color: #6b7280;
          }
          
          .control-btn:hover {
            background: #f3f4f6;
            color: #374151;
          }
          
          .control-btn.edit-mode-disabled {
            opacity: 0.5;
            cursor: not-allowed;
            pointer-events: none;
          }
          
          .primary-btn {
            background: #3b82f6;
            color: white;
          }
          
          .primary-btn:hover {
            background: #2563eb;
          }
          
          .btn-save {
            background: #10b981;
          }
          
          .btn-save:hover {
            background: #059669;
          }
          
          .btn-edit-cancel {
            background: #6b7280;
          }
          
          .btn-edit-cancel:hover {
            background: #4b5563;
          }
          
          .divider {
            width: 1px;
            height: 24px;
            background: #d1d5db;
          }
          
          .version-dropdown {
            position: relative;
          }
          
          .version-btn {
            padding: 6px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            color: #374151;
            cursor: pointer;
            max-width: 300px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          
          .version-btn.edit-mode-disabled {
            opacity: 0.5;
            cursor: not-allowed;
            pointer-events: none;
          }
          
          .version-dropdown-menu {
            position: absolute;
            top: 100%;
            right: 0;
            margin-top: 4px;
            background: white;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
            z-index: 1000;
            min-width: 300px;
            max-height: 400px;
            overflow-y: auto;
          }
          
          .dropdown-header {
            padding: 12px;
            border-bottom: 1px solid #e5e7eb;
            display: flex;
            gap: 8px;
          }
          
          .dropdown-btn {
            padding: 6px 12px;
            border: none;
            border-radius: 4px;
            font-size: 12px;
            font-weight: 500;
            cursor: pointer;
          }
          
          .dropdown-btn-primary {
            background: #ddd;
            color: #222;
          }
            .dropdown-btn-primary:hover {
              background: #ccc;
            }
          
          .dropdown-btn-danger {
            background: #ef4444;
            color: white;
          }
          
          .dropdown-btn-disabled {
            background: #f3f4f6;
            color: #9ca3af;
            cursor: not-allowed;
          }
          
          .version-list {
            max-height: 300px;
            overflow-y: auto;
          }
          
          .version-item {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 8px 12px;
            cursor: pointer;
            font-size: 13px;
          }
          
          .version-item:hover {
            background: #f3f4f6;
          }
          
          .version-item.current {
            background: #dbeafe;
            color: #1d4ed8;
          }
          
          .version-checkbox {
            width: 16px;
            height: 16px;
          }
          
          .version-text {
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          
          .star {
            color: #fbbf24;
          }
          
          .star-dimmed {
            color: #d1d5db;
          }
          
          .favorite-input-box {
            position: fixed;
            top: 80px;
            right: 24px;
            background: white;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            padding: 16px;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
            z-index: 1001;
            min-width: 300px;
          }
          
          .favorite-input-form {
            display: flex;
            gap: 8px;
            align-items: center;
          }
          
          .favorite-input {
            flex: 1;
            padding: 8px 12px;
            border: 1px solid #d1d5db;
            border-radius: 4px;
            font-size: 14px;
          }
          
          .favorite-btn {
            padding: 8px 12px;
            border: none;
            border-radius: 4px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
          }
          
          .favorite-btn-save {
            background: #10b981;
            color: white;
          }
          
          .favorite-btn-save:disabled {
            background: #d1d5db;
            cursor: not-allowed;
          }
          
          .favorite-btn-close {
            background: #f3f4f6;
            color: #6b7280;
            padding: 8px;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        `}
      </style>
      
      <div className="top-header">
        {/* 왼쪽 영역 - 범례 필터 */}
        <div className="header-left">
          <div className="legend-container">
            <button
              className={`legend-btn ${!legendFilters.pxd ? 'disabled' : ''} ${isEditMode ? 'edit-mode-disabled' : ''}`}
              onClick={() => !isEditMode && onLegendToggle('pxd')}
            >
              <div className="legend-color lg-pxd" />
              <span>pxd 인원</span>
            </button>
            <button
              className={`legend-btn ${!legendFilters.external ? 'disabled' : ''} ${isEditMode ? 'edit-mode-disabled' : ''}`}
              onClick={() => !isEditMode && onLegendToggle('external')}
            >
              <div className="legend-color lg-ext" />
              <span>외주/계약/레이블</span>
            </button>
            <button
              className={`legend-btn ${!legendFilters.intern ? 'disabled' : ''} ${isEditMode ? 'edit-mode-disabled' : ''}`}
              onClick={() => !isEditMode && onLegendToggle('intern')}
            >
              <div className="legend-color lg-intern" />
              <span>인턴</span>
            </button>
            <button
              className={`legend-btn ${!legendFilters.planned ? 'disabled' : ''} ${isEditMode ? 'edit-mode-disabled' : ''}`}
              onClick={() => !isEditMode && onLegendToggle('planned')}
            >
              <div className="legend-color lg-planned" />
              <span>자리 예정 인원</span>
            </button>
          </div>
        </div>
        
        {/* 오른쪽 영역 - 기존 플로팅 버튼들 */}
        <div className="header-right">
          {isEditMode ? (
            <>
              <button className="primary-btn btn-save" onClick={onSave}>
                저장
              </button>
              <button
                className="primary-btn btn-edit-cancel"
                onClick={() => onEditModeCancel ? onEditModeCancel() : onEditModeToggle(false)}
              >
                취소
              </button>
            </>
          ) : (
            <button
              className="primary-btn"
              onClick={() => onEditModeToggle(true)}
            >
              수정
            </button>
          )}
          
          {/* 버전 선택 드롭다운 */}
          <div className="version-dropdown">
            <button
              className={`version-btn ${isEditMode ? 'edit-mode-disabled' : ''}`}
              onClick={() => !isEditMode && onVersionDropdownToggle()}
            >
              {currentVersion ? getVersionDisplayText(currentVersion) : '버전 선택'}
            </button>
            
            {showVersionDropdown && (
              <div className="version-dropdown-menu">
                {/* 헤더 */}
                <div className="dropdown-header">
                  <button
                    className="dropdown-btn dropdown-btn-primary"
                    onClick={() => {
                      const nonFavoriteVersions = versions.filter(v => !v.isFavorite || !v.alias);
                      const versionIds = nonFavoriteVersions.map(v => v.id);
                      versionIds.forEach(id => onVersionSelect(id));
                    }}
                  >
                    임시 항목 선택
                  </button>
                  <button
                    className={`dropdown-btn ${
                      selectedVersions.length > 0 
                        ? 'dropdown-btn-danger' 
                        : 'dropdown-btn-disabled'
                    }`}
                    onClick={onBulkDeleteVersions}
                    disabled={selectedVersions.length === 0}
                  >
                    삭제 ({selectedVersions.length})
                  </button>
                </div>
                
                {/* 버전 목록 */}
                <div className="version-list">
                  {versions.map((v) => (
                    <div
                      key={v.id}
                      className={`version-item ${
                        currentVersion?.id === v.id ? 'current' : ''
                      }`}
                      onClick={() => {
                        onVersionChange({ target: { value: v.id } });
                        onVersionDropdownToggle();
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedVersions.includes(v.id)}
                        onChange={(e) => {
                          e.stopPropagation();
                          onVersionSelect(v.id);
                        }}
                        className="version-checkbox"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="version-text">{getVersionDisplayText(v)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* 현재 버전 삭제 버튼 */}
          {currentVersion && (
            <button
              className={`control-btn ${isEditMode ? 'edit-mode-disabled' : ''}`}
              onClick={() => !isEditMode && onDeleteVersion()}
              title="현재 버전 삭제"
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          )}
          
          {/* 즐겨찾기 버튼 */}
          {currentVersion && (
            currentVersion.isFavorite ? (
              <button
                className={`control-btn star-dimmed ${isEditMode ? 'edit-mode-disabled' : ''}`}
                onClick={() => !isEditMode && onRemoveFavorite()}
                title="즐겨찾기 해제"
              >
                <i className="ri-star-fill"></i>
              </button>
            ) : (
              <button
                className={`control-btn star ${isEditMode ? 'edit-mode-disabled' : ''}`}
                onClick={() => !isEditMode && onFavoriteInputToggle()}
                title="즐겨찾기 추가"
              >
                <i className="ri-star-line"></i>
              </button>
            )
          )}
          
          <div className="divider" />
          
          <button
            className="control-btn"
            onClick={onOpenPersonManagement}
            title="인원 관리"
          >
            <i className="ri-table-line"></i>
            테이블
          </button>
        </div>
      </div>
      
      {/* 즐겨찾기 별칭 입력 플로팅 박스 */}
      {showFavoriteInput && (
        <div className="favorite-input-box">
          <div className="favorite-input-form">
            <input
              className="favorite-input"
              placeholder="별칭을 입력하세요"
              value={favoriteAlias}
              onChange={(e) => onFavoriteAliasChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  onAddFavorite();
                }
                if (e.key === 'Escape') {
                  onFavoriteInputToggle();
                }
              }}
              autoFocus
            />
            <button
              className="favorite-btn favorite-btn-save"
              onClick={onAddFavorite}
              disabled={!favoriteAlias.trim()}
            >
              저장
            </button>
            <button
              className="favorite-btn-close"
              onClick={onFavoriteInputToggle}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TopHeader; 